/**
 * @fileoverview Module code convention — the single mechanical rule that
 *               derives a module's `code` from its display `name`.
 * @module core-domain/modules/moduleCode
 *
 * Responsibilities:
 * - Derive module codes: lowerCamelCase of the full module name.
 * - Validate that a candidate code conforms to the convention.
 *
 * Dependencies:
 * - None — pure function.
 *
 * Architecture:
 * - `code` is the stable, cross-environment module identity. It is always
 *   derived from the module name — never hand-picked, abbreviated, or
 *   translated at runtime. Every write path (catalog editor, REST API, seed
 *   import) must use this same derivation so codes cannot drift.
 * - Convention: split the name on non-alphanumeric separators; lowercase the
 *   first word entirely; capitalize the first letter of every subsequent
 *   word; no spaces, dashes, or underscores.
 *   Examples: "Hydrant Management" -> `hydrantManagement`,
 *   "Permits & Certificates" -> `permitsCertificates`,
 *   "TCE Integration" -> `tceIntegration`.
 *
 * Notes:
 * - A matching implementation exists in api/packages/core — the two package
 *   trees cannot import each other, so parity is enforced by tests.
 */

/**
 * Splits a display name into words on non-alphanumeric boundaries.
 * '@'/'&'/'-'_'/'.' etc. are all separators; diacritics are stripped.
 */
function moduleNameWords(name: string): string[] {
  return String(name ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // strip combining diacritics after NFKD
    .split(/[^A-Za-z0-9]+/)
    .filter((word) => word.length > 0);
}
/**
 * Acronyms that must survive name normalization verbatim. Everything else is
 * title-cased, so "wildland FIRE" -> "Wildland Fire" (shouting corrected)
 * while "TCE Integration" and "Advanced AI" keep their acronym spelling.
 */
const KNOWN_ACRONYMS = new Set(['TCE', 'AI']);

/**
 * Normalizes a module display name to the standard form the code is derived
 * from: leading/trailing punctuation and whitespace removed, internal
 * whitespace collapsed to single spaces, and every word capitalized (first
 * letter upper, remaining letters lower) — except known acronyms, which are
 * forced to their canonical uppercase spelling. Internal separators such as
 * '&' are preserved so "Permits & Certificates" stays "Permits & Certificates".
 */
export function normalizeModuleName(name: string): string {
  const cleaned = String(name ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // strip combining diacritics after NFKD
    .replace(/[\s\p{P}]+$/u, '') // trailing punctuation/whitespace
    .replace(/^[\s\p{P}]+/u, '') // leading punctuation/whitespace
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned
    .split(' ')
    .filter((word) => word.length > 0)
    .map((word) => {
      const upper = word.toUpperCase();
      if (word.length > 1 && KNOWN_ACRONYMS.has(upper)) return upper;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}


/**
 * Derives the module code from a module name.
 * Returns '' when the name carries no alphanumeric words.
 */
export function moduleCodeFromName(name: string): string {
  const words = moduleNameWords(name);
  if (words.length === 0) return '';
  const [first, ...rest] = words;
  return (
    first.toLowerCase() +
    rest.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join('')
  );
}

/**
 * Whether a candidate code satisfies the module-code convention:
 * starts lowercase, letters/digits only, no separators. This is a format
 * check only — the authoritative code is always `moduleCodeFromName(name)`.
 */
export function isModuleCodeFormat(code: string): boolean {
  return /^[a-z][A-Za-z0-9]*$/.test(String(code ?? ''));
}
